<script context="module">
  import { writable } from 'svelte/store'

  export const INTERCOM_ID = 'u4ht3h8q'

  export const isOpen = writable(false)

  export const isHidden = writable(false)

  export function show() {
    if (typeof Intercom === 'function') Intercom('show')
  }

  export function hide() {
    if (typeof Intercom === 'function') Intercom('hide')
  }

  export function showNewMessage() {
    if (typeof Intercom === 'function') Intercom('showNewMessage')
  }

  /**
   * @param {object} props
   * @param {string} [props.email]
   * @param {string} [props.user_id]
   * @param {string} [props.name]
   * @param {string} [props.phone]
   */
  export function update(props) {
    if (typeof Intercom === 'function') Intercom('update', props)
  }
</script>

<script>
  import { scale } from 'svelte/transition'
  import { cubicOut } from 'svelte/easing'
  import { page } from '$app/stores'
  import { onMount } from 'svelte'

  import { getLanguage } from './context.js'

  const language = getLanguage()

  let timeout
  let injected = false
  const load = () => {
    if (injected) return
    injected = true

    // Clean up
    clearTimeout(timeout)
    window.removeEventListener('click', load)
    window.removeEventListener('scroll', load)
    window.removeEventListener('touchstart', load)

    // Load script
    const script = document.createElement('script')
    script.async = true
    script.defer = true
    script.src = 'https://widget.intercom.io/widget/u4ht3h8q'
    document.head.appendChild(script)
  }

  const unsubscribe = isOpen.subscribe((value) => {
    if (value && !injected) {
      unsubscribe()
      load()
    }
  })

  onMount(() => {
    window.addEventListener('click', load)
    window.addEventListener('scroll', load, { passive: true })
    window.addEventListener('touchstart', load)
    timeout = setTimeout(load, 20000)

    // Bootstrap
    if (typeof window.Intercom !== 'function') {
      window.Intercom = Object.assign(
        function () {
          window.Intercom.c(arguments)
        },
        {
          q: [],
          c(args) {
            window.Intercom.q.push(args)
          }
        }
      )
    }

    const client = document.cookie.match(/(?:^|;\s*)aa.client=(.+?)(?:;|$)/)
    const session = document.cookie.match(/(?:^|;\s*)aa.session=(.+?)(?:;|$)/)

    // Set defaults
    Intercom('boot', {
      app_id: INTERCOM_ID,
      hide_default_launcher: true,
      client_id: client?.[1],
      session_id: session?.[1]
    })

    if ($page.data.user) {
      Intercom('update', {
        user_id: $page.data.user.id,
        name: $page.data.user.name,
        email: $page.data.user.email
      })
    }

    // Handle deep linking
    if ($page.url.searchParams.has('notification_link_uid')) {
      load()
    }

    language.subscribe(($language) => {
      Intercom('update', {
        language_override: $language
      })
    })

    isOpen.subscribe(($isOpen) => {
      Intercom($isOpen ? 'show' : 'hide')
    })

    // Sync state
    Intercom('onHide', () => ($isOpen = false))
    Intercom('onShow', () => ($isOpen = true))
  })
</script>

{#if !$isHidden}
  <button
    in:scale={{
      duration: 250,
      start: 0.25,
      opacity: 0.5,
      easing: cubicOut
    }}
    out:scale={{
      duration: 250,
      start: 0.25,
      opacity: 0,
      easing: cubicOut
    }}
    class="intercom"
    on:click|preventDefault={() => ($isOpen = !$isOpen)}>
    Open support chat
    <svg class="icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 28 32">
      <path
        fill="currentColor"
        d="M28 32s-4.714-1.855-8.527-3.34H3.437C1.54 28.66 0 27.026 0 25.013V3.644C0 1.633 1.54 0 3.437 0h21.125c1.898 0 3.437 1.632 3.437 3.645v18.404H28V32zm-4.139-11.982a.88.88 0 00-1.292-.105c-.03.026-3.015 2.681-8.57 2.681-5.486 0-8.517-2.636-8.571-2.684a.88.88 0 00-1.29.107 1.01 1.01 0 00-.219.708.992.992 0 00.318.664c.142.128 3.537 3.15 9.762 3.15 6.226 0 9.621-3.022 9.763-3.15a.992.992 0 00.317-.664 1.01 1.01 0 00-.218-.707z" />
    </svg>
  </button>
{/if}

<style>
  .intercom {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 3rem;
    height: 3rem;
    padding: 1rem;
    position: fixed;
    bottom: 1.5rem;
    font-size: 0;
    right: 1.5rem;
    z-index: 1;
    color: transparent;
    background: rgb(var(--rgb-red));
    border-radius: var(--border-radius-full);
    box-shadow: var(--box-shadow-sm);
    transform: translateY(calc(var(--current-scroll-margin-bottom) * -1));
    transition: transform 250ms cubic-bezier(0.33, 1, 0.68, 1);
  }

  @media (min-width: 1000px) {
    .intercom {
      width: 3.5rem;
      height: 3.5rem;
      bottom: 2rem;
      right: 2rem;
    }
  }

  .intercom:hover {
    box-shadow: var(--box-shadow-sm-hover);
  }

  .intercom:active {
    box-shadow: var(--box-shadow-sm-active);
  }

  .icon {
    display: block;
    width: 100%;
    height: auto;
    color: rgb(var(--rgb-white));
  }
</style>
